/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/cart/api';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { state } from '@/cart/lib/state';
import { CartModel } from '@/cart/data/models';
import { transformCart } from '@/cart/data/transforms';

import { GUEST_CART_QUERY, CUSTOMER_CART_QUERY } from './graphql/CartQuery';

export const getCartData = async (): Promise<CartModel | null> => {
  const authenticated = state.authenticated;
  const cartId = state.cartId;

  // Customer Cart
  if (authenticated) {
    return fetchGraphQl(CUSTOMER_CART_QUERY, { method: 'POST' }).then(
      ({ errors, data }) => {
        if (errors) return handleFetchError(errors);

        const merged = { ...data.cart, ...data.customer };

        return transformCart(merged);
      }
    );
  }

  // Guest Cart
  if (!cartId) throw new Error('No cart ID found');

  return fetchGraphQl(GUEST_CART_QUERY, {
    method: 'POST',
    cache: 'no-cache',
    variables: { cartId },
  }).then(({ errors, data }) => {
    if (errors) return handleFetchError(errors);

    return transformCart(data.cart);
  });
};
